import React, { useState } from 'react';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import UGCLayout from '@/components/layouts/UGCLayout';

const plans = [
  {
    id: 'monthly',
    name: 'Elite Monthly',
    price: '$99',
    period: '/month',
    desc: 'Full access, billed monthly',
    features: [
      'UGC Dashboard, Deal Tracker, Sequences',
      'Scripts, Shot Lists, Outreach, Invoices',
      'Analyzer credits + advanced feedback engine',
      'Priority processing and support',
    ],
    badge: null,
    whopCheckout: 'plan_0BaO8IscYTvfS',
  },
  {
    id: 'annual',
    name: 'Elite Annual',
    price: '$999',
    period: '/year',
    desc: 'Best value - save ~2 months',
    features: [
      'Everything in Monthly',
      'Save $189 vs monthly billing',
      'Lock in current pricing',
      'Same full access and support',
    ],
    badge: 'Most popular',
    whopCheckout: 'plan_Q9XYlNHreWOJU',
  },
  {
    id: 'lifetime',
    name: 'Lifetime Elite',
    price: '$2,500',
    period: 'one-time',
    desc: 'Limited to 100 founders',
    features: [
      'Everything in Elite forever',
      'All future updates included',
      'Founders badge and concierge support',
      'Priority processing permanently',
    ],
    badge: 'Limited',
    whopCheckout: 'plan_HgQhs2uICD7u8',
    lifetime: true,
  },
];

const UGCPricing = () => {
  const lifetimeLeft = 10; // artificial counter for urgency; replace with live count when available
  const [showMenu, setShowMenu] = useState(false);

  const handleSelect = (plan) => {
    const productId = plan.whopCheckout;
    if (productId && !productId.startsWith('REPLACE')) {
      window.location.href = `https://whop.com/checkout/${productId}?d2c=true`;
    } else {
      alert('Product ID not configured yet.');
    }
  };

  return (
    <UGCLayout className="pb-20">
      {/* Hide any legacy mobile tab bar on this marketing page */}
      <style>{`.mobile-tab-bar { display: none !important; }`}</style>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/30 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10"
              onClick={() => setShowMenu(!showMenu)}
              aria-label="Open menu"
            >
              <Icon name={showMenu ? 'X' : 'Menu'} size={18} color="white" />
            </button>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Icon name="Sparkles" size={20} color="white" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">Earn My Edge</p>
              <p className="text-xs text-amber-200">UGC Creator Platform</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-amber-100 hover:text-white hover:bg-white/10"
              onClick={() => (window.location.href = '/ugc')}
            >
              Overview
            </Button>
            <Button 
              size="sm" 
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => (window.location.href = '/ugc-authentication?mode=login')}
            >
              Sign In
            </Button>
          </div>
        </div>
        {showMenu && (
          <div className="absolute left-0 right-0 top-full bg-slate-900/90 backdrop-blur border-t border-white/10 shadow-md md:hidden z-20">
            <div className="px-4 py-3 space-y-2">
              <Button variant="ghost" className="w-full justify-start text-amber-100 hover:text-white hover:bg-white/10" onClick={() => (window.location.href = '/ugc')}>
                Overview
              </Button>
              <Button variant="ghost" className="w-full justify-start text-amber-100 hover:text-white hover:bg-white/10" onClick={() => (window.location.href = '/ugc-authentication?mode=login')}>
                Sign In
              </Button>
              <Button variant="ghost" className="w-full justify-start text-amber-100 hover:text-white hover:bg-white/10" onClick={() => setShowMenu(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 py-12 mb-20">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/40 mb-4">
            <Icon name="Video" size={16} color="#FCD34D" />
            <span className="text-xs font-semibold text-amber-100">UGC Creator Plans</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">Choose Your Creator Plan</h1>
          <p className="text-amber-100/80 max-w-2xl mx-auto">
            Scripts, outreach, invoices, deal tracking, leaderboards, teleprompter, and creator intel — all in one place.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border ${plan.badge ? 'border-amber-500 ring-2 ring-amber-500/20' : 'border-white/10'} bg-slate-800/40 p-6 shadow-sm flex flex-col hover:bg-slate-800/60 transition-colors`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500/20 border border-amber-500/40 rounded-full text-xs font-semibold text-amber-100">
                  {plan.badge}
                </div>
              )}
              {plan.lifetime && (
                <div className="absolute -top-3 right-3 px-3 py-1 bg-amber-500 text-white rounded-full text-xs font-semibold">
                  {lifetimeLeft} left
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <p className="text-lg font-semibold text-white">{plan.name}</p>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {plan.price}
              </p>
              <p className="text-sm text-amber-100/60 mb-4">{plan.period}</p>
              <p className="text-sm text-amber-100/80 mb-4">{plan.desc}</p>
              <ul className="space-y-2 text-sm text-amber-100/90 mb-6">
                {plan.features.map((inc) => (
                  <li key={inc} className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex w-5 h-5 items-center justify-center rounded-md bg-amber-500/20">
                      <Icon name="Check" size={14} color="#FCD34D" />
                    </span>
                    <span>{inc}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className={`w-full mt-auto ${plan.badge ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'border-amber-500/40 text-amber-100 hover:bg-white/10'}`}
                onClick={() => handleSelect(plan)}
              >
                {plan.lifetime ? 'Join Founders' : 'Select Plan'}
              </Button>
            </div>
          ))}
        </div>
      </main>
    </UGCLayout>
  );
};

export default UGCPricing;




